import { useMemo, useState } from "react";
import { ShieldCheck, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  useWaiversOfSubrogation,
  useDeleteWaiverOfSubrogation,
  type WaiverOfSubrogationLink,
} from "@/lib/api";
import { WaiverOfSubrogationDialog } from "./WaiverOfSubrogationDialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/formatters";
import { ASSET_TABLE_COLUMN_WIDTHS } from "./asset-helpers";
import type { Policy, PolicyWaiverOfSubrogation } from "@shared/schema";

const HEAD_CLASS =
  "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
const CELL_CLASS = "px-6 py-4";
const ACTIONS_CELL_CLASS = "py-4 pr-6 text-right";

interface Props {
  insuredId: string;
  policies?: Policy[];
  // When set, narrow the rendered list to WoS that belong to this policy:
  // either linked via coverageIds (composite id `<policyId>:<coverageKey>`) OR
  // carrier-scraped from this policy (name present in policyWaiversOfSubrogation
  // below). The latter surfaces sync-created PENDING records that have no
  // coverage link yet — mirroring how AdditionalInsuredsSection shows the
  // policy's scraped additional insureds (CSR-1456).
  policyId?: string;
  // Carrier-scraped WoS snapshot for the scoped policy (policies.waivers_of_
  // subrogation JSONB). Used in variant="policy" to surface the scraped records
  // before an agent assigns coverages.
  policyWaiversOfSubrogation?: PolicyWaiverOfSubrogation[] | null;
  // Wrapper variant. "insured" (default) wraps in a Card to match the Insured
  // Assets tab; "policy" wraps in a flat <section> to match the Policy Assets
  // tab siblings.
  variant?: "policy" | "insured";
}

// CSR-1101 — Assets-tab Waivers of Subrogation section. Mirrors
// LossPayeesSection: the pending row shows the orange "PENDING ADDITIONAL
// INFORMATION" chip in place of the email. Same component backs both the
// Insured and Policy Assets tabs (pass `policyId` + `variant="policy"`).
export function WaiversOfSubrogationSection({
  insuredId,
  policies = [],
  policyId,
  policyWaiversOfSubrogation,
  variant = "insured",
}: Props) {
  const { toast } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const { data: links, isLoading } = useWaiversOfSubrogation(insuredId);
  const deleteMutation = useDeleteWaiverOfSubrogation(insuredId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWos, setEditingWos] = useState<WaiverOfSubrogationLink | null>(null);

  const items = links ?? [];

  const filteredItems = useMemo(() => {
    if (!policyId) return items;
    // Names carrier-scraped from THIS policy (policies.waivers_of_subrogation).
    // Sync materializes them into pending records with no coverage link yet, so
    // a coverage-only filter would hide them — include them by name too.
    const scrapedNames = new Set(
      (policyWaiversOfSubrogation ?? [])
        .map((w) => w.name?.trim().toLowerCase())
        .filter((n): n is string => !!n),
    );
    return items.filter(
      (wos) =>
        (wos.coverageIds ?? []).some((cid) => cid.startsWith(`${policyId}:`)) ||
        scrapedNames.has(wos.name.trim().toLowerCase()),
    );
  }, [items, policyId, policyWaiversOfSubrogation]);

  // CSR-1456 — policy-level "Blanket Waiver of Subrogation Endorsement" Yes/No,
  // carrier-synced (read-only). Only meaningful in the policy variant, where
  // `policies` holds the single policy this section is scoped to. null = the
  // carrier flag wasn't scraped (rendered as "--").
  const blanketWaiver =
    variant === "policy"
      ? ((policyId ? policies.find((p) => p.id === policyId) : policies[0])
          ?.blanketWaiverOfSubrogation ?? null)
      : null;

  const handleDelete = async (e: React.MouseEvent, link: WaiverOfSubrogationLink) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Remove Waiver of Subrogation",
      description: `Remove "${link.name}" as a Waiver of Subrogation?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(link.id);
      toast({ title: "Deleted", description: "Waiver of Subrogation removed." });
    } catch (err) {
      toast({
        title: "Error",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleRowClick = (link: WaiverOfSubrogationLink) => {
    setEditingWos(link);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingWos(null);
    setDialogOpen(true);
  };

  const addButton = (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      onClick={handleAdd}
      data-testid="waivers-of-subrogation-add"
    >
      <Plus className="w-4 h-4" /> Add
    </Button>
  );

  const header = (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-foreground" aria-hidden="true" />
        <h3 className="text-base font-semibold">Waivers of Subrogation</h3>
      </div>
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-muted text-xs font-medium text-muted-foreground"
          data-testid="waivers-of-subrogation-count"
        >
          {filteredItems.length}
        </span>
        {addButton}
      </div>
    </div>
  );

  const tableContent = (
    <>
      {isLoading ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-muted-foreground">
          No waivers of subrogation linked.
        </div>
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[0]}`}>Name</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[1]}`}>Email</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[2]}`}>COIs Sent</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[3]}`}>Last Sent</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[4]}`}>Created At</TableHead>
              <TableHead className={`${HEAD_CLASS} ${ASSET_TABLE_COLUMN_WIDTHS[5]}`}>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((link) => (
              <TableRow
                key={link.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(link)}
                data-testid={`waiver-of-subrogation-row-${link.id}`}
              >
                <TableCell className={`${CELL_CLASS} font-medium`}>
                  <div>{link.name}</div>
                  {/* CSR-1456 — mirror Additional Insureds: show the address as
                      a sub-line under the name when the carrier supplied one
                      (even while the record is still PENDING coverages). */}
                  {link.street && link.city && link.state && link.zip ? (
                    <div
                      className="text-xs font-normal text-muted-foreground mt-0.5"
                      data-testid="waiver-of-subrogation-address"
                    >
                      {link.street}, {link.city}, {link.state} {link.zip}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {link.pendingAdditionalInfo ? (
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase bg-amber-500/10 text-amber-700 dark:text-amber-400"
                      data-testid="waiver-of-subrogation-pending-chip"
                    >
                      Pending Additional Information
                    </span>
                  ) : (
                    link.email || "—"
                  )}
                </TableCell>
                <TableCell className={CELL_CLASS}>
                  {link.pendingAdditionalInfo || link.coiCount === null ? (
                    // CSR-2212 — pending rows never had a count; a null one
                    // means the COI service could not be reached just now.
                    // Both read as "no number to show", never as zero.
                    <span
                      title={
                        link.coiCount === null && !link.pendingAdditionalInfo
                          ? "COI history is temporarily unavailable"
                          : undefined
                      }
                    >
                      —
                    </span>
                  ) : (
                    <Badge variant="outline" className="rounded-full">
                      {link.coiCount}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {link.pendingAdditionalInfo || link.coiCount === null
                    ? "—"
                    : link.lastSentAt
                      ? formatDate(link.lastSentAt)
                      : "Never"}
                </TableCell>
                <TableCell className={`${CELL_CLASS} text-muted-foreground`}>
                  {link.createdAt ? formatDate(link.createdAt) : "—"}
                </TableCell>
                <TableCell className={ACTIONS_CELL_CLASS}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={(e) => handleDelete(e, link)}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );

  const dialog = (
    <WaiverOfSubrogationDialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setEditingWos(null);
      }}
      insuredId={insuredId}
      policies={policies}
      editData={editingWos}
    />
  );

  if (variant === "insured") {
    return (
      <>
        <Card className="overflow-hidden" data-testid="waivers-of-subrogation-section">
          {header}
          {tableContent}
        </Card>
        {dialog}
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <section
        className="rounded-xl border border-border bg-card overflow-hidden"
        data-testid="policy-waivers-of-subrogation"
      >
        {header}
        <div
          className="flex items-center justify-between gap-2 border-t border-border px-6 py-3 text-sm"
          data-testid="blanket-waiver-of-subrogation"
        >
          <span className="text-muted-foreground">
            Blanket Waiver of Subrogation Endorsement
          </span>
          <span
            className="font-medium text-foreground"
            data-testid="blanket-waiver-of-subrogation-value"
          >
            {blanketWaiver === null ? "—" : blanketWaiver ? "Yes" : "No"}
          </span>
        </div>
        {tableContent}
      </section>
      {dialog}
      {confirmDialog}
    </>
  );
}
